import {
  LIFECYCLE_GROUP,
  LIFECYCLE_LABEL,
  type LotStage,
} from "@/features/animals/lifecycleStates";
import { Pencil } from "lucide-react";
import type { Lot } from "../types";

type Props = {
  lots: Lot[];
  onEdit: (lot: Lot) => void;
};

export function LotsList({ lots, onEdit }: Props) {
  return (
    <>
      {/* Mobile cards */}
      <ul className="space-y-2 md:hidden">
        {lots.map((lot) => (
          <li key={lot.id}>
            <button
              type="button"
              onClick={() => onEdit(lot)}
              className={`flex w-full items-center gap-3 rounded-2xl border border-app-border px-4 py-3 text-left transition-colors hover:bg-app-surface-2 ${
                lot.isActive ? "bg-app-surface" : "bg-app-surface opacity-60"
              }`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate font-medium text-[14px] text-app-ink-1">
                    {lot.name}
                  </span>
                  {!lot.isActive && (
                    <span className="rounded-md bg-neutral-100 px-1.5 py-0.5 font-medium text-[10px] text-neutral-500 uppercase tracking-wide">
                      Inactivo
                    </span>
                  )}
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  <StageBadge stage={lot.stage} />
                  <span className="text-[11px] text-app-ink-3">
                    {lot.activeAnimalCount} {lot.activeAnimalCount === 1 ? "animal" : "animales"}
                  </span>
                </div>
              </div>
              <Pencil size={16} className="shrink-0 text-app-ink-3" aria-hidden />
            </button>
          </li>
        ))}
      </ul>

      {/* Desktop table */}
      <div className="hidden overflow-hidden rounded-2xl border border-app-border bg-app-surface md:block">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="border-app-border border-b">
              <th className="px-4 py-3 text-left font-medium text-[11px] text-app-ink-3 uppercase tracking-[0.06em]">
                Nombre
              </th>
              <th className="px-4 py-3 text-left font-medium text-[11px] text-app-ink-3 uppercase tracking-[0.06em]">
                Etapa
              </th>
              <th className="px-4 py-3 text-right font-medium text-[11px] text-app-ink-3 uppercase tracking-[0.06em]">
                Animales activos
              </th>
              <th className="px-4 py-3 text-left font-medium text-[11px] text-app-ink-3 uppercase tracking-[0.06em]">
                Estado
              </th>
              <th className="px-4 py-3" aria-label="Acciones" />
            </tr>
          </thead>
          <tbody className="divide-y divide-app-border">
            {lots.map((lot) => (
              <tr
                key={lot.id}
                className={`transition-colors hover:bg-app-surface-2 ${
                  lot.isActive ? "" : "opacity-60"
                }`}
              >
                <td className="px-4 py-3">
                  <button
                    type="button"
                    onClick={() => onEdit(lot)}
                    className="block w-full text-left font-medium text-app-ink-1 hover:text-app-emerald"
                  >
                    {lot.name}
                  </button>
                </td>
                <td className="px-4 py-3">
                  <StageBadge stage={lot.stage} />
                </td>
                <td className="px-4 py-3 text-right text-app-ink-2">{lot.activeAnimalCount}</td>
                <td className="px-4 py-3">
                  {lot.isActive ? (
                    <span className="text-[12px] text-app-ink-3">Activo</span>
                  ) : (
                    <span className="rounded-md bg-neutral-100 px-1.5 py-0.5 font-medium text-[10px] text-neutral-500 uppercase tracking-wide">
                      Inactivo
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    type="button"
                    onClick={() => onEdit(lot)}
                    aria-label={`Editar ${lot.name}`}
                    className="inline-flex size-8 items-center justify-center rounded-lg text-app-ink-3 hover:bg-app-surface hover:text-app-ink-1"
                  >
                    <Pencil size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

const STAGE_GROUP_CLASSES = {
  cria: "bg-blue-100 text-blue-700",
  desarrollo: "bg-violet-100 text-violet-700",
  productiva: "bg-app-emerald-bg text-app-emerald",
  retirada: "bg-neutral-100 text-neutral-500",
} as const;

function StageBadge({ stage }: { stage: LotStage }) {
  const group = LIFECYCLE_GROUP[stage];
  const classes = STAGE_GROUP_CLASSES[group];
  return (
    <span className={`inline-block rounded-xl px-2 py-0.5 font-medium text-[11px] ${classes}`}>
      {LIFECYCLE_LABEL[stage]}
    </span>
  );
}
